/* global URL_MENU */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import useModules from '@utils/hooks/useModules';
import { ModuleName } from '@utils/constants';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import { openExternalUrl } from '@utils/navigationExternalUrl';
import { EXTERNAL_URLS } from '@utils/constants';
import { closeMenu, getNavigatePath } from '../utils/menuHelpers';
import { MenuItem } from '../types/menu.types';
import {
  IconShare,
  IconTicket,
  IconFileText,
  IconBuildingStore,
  IconHelpCircle,
  IconExternalLink,
  IconLogout,
  IconClock,
  IconReceiptDollar,
  IconUsers,
  IconSettings,
  IconCloudCheck,
  IconBuildingBridge2,
  IconLinkPlus,
  IconMessage2Filled,
} from '@tabler/icons-react';

export const useMenuItems = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { commerce } = useGetCommerce();
  const { hasModule } = useModules();
  const { isFreemium } = useSubscription();
  const { trackEvent } = useTracking();

  const mainMenuItems: MenuItem[] = useMemo(
    () => [
      {
        id: 'mi-cuenta',
        label: 'Mi cuenta Plick',
        icon: <IconBuildingStore />,
        onClick: () => {
          navigate('mi-cuenta');
          closeMenu();
        },
        show: true,
      },
      {
        id: 'perfil-social',
        label: 'Perfil Social',
        icon: <IconLinkPlus />,
        onClick: () => {
          navigate(getNavigatePath(hasModule(ModuleName.LINK_PROFILE), 'perfil-social'));
          closeMenu();
        },
        show: isFreemium || Boolean(hasModule(ModuleName.LINK_PROFILE)),
        notIncludedOnCurrentPlan: !hasModule(ModuleName.LINK_PROFILE),
      },
      {
        id: 'compartir-menu',
        label: 'Compartir menú',
        icon: <IconShare />,
        onClick: () => {
          navigate(getNavigatePath(hasModule(ModuleName.COMPARTIR), 'compartir-menu'));
          closeMenu();
        },
        show: isFreemium || Boolean(hasModule(ModuleName.COMPARTIR)),
        notIncludedOnCurrentPlan: !hasModule(ModuleName.COMPARTIR),
      },
      {
        id: 'datos-del-comercio',
        label: 'Datos del comercio',
        icon: <IconBuildingStore />,
        onClick: () => {
          navigate('datos-del-comercio');
          closeMenu();
        },
        show: isFreemium || Boolean(hasModule(ModuleName.DATOS_COMERCIO)),
        notIncludedOnCurrentPlan: !hasModule(ModuleName.DATOS_COMERCIO),
      },
      {
        id: 'horarios',
        label: 'Horarios',
        icon: <IconClock />,
        onClick: () => {
          navigate(getNavigatePath(hasModule(ModuleName.HORARIOS), 'horarios'));
          closeMenu();
        },
        show: isFreemium || Boolean(hasModule(ModuleName.HORARIOS)),
        notIncludedOnCurrentPlan: !hasModule(ModuleName.HORARIOS),
      },
      {
        id: 'tarifas',
        label: 'Tarifas y entregas',
        icon: <IconReceiptDollar />,
        onClick: () => {
          navigate(getNavigatePath(hasModule(ModuleName.TARIFAS), 'tarifas'));
          closeMenu();
        },
        show: isFreemium || Boolean(hasModule(ModuleName.TARIFAS)),
        notIncludedOnCurrentPlan: !hasModule(ModuleName.TARIFAS),
      },
      {
        id: 'mesas',
        label: 'Mesas',
        icon: <IconBuildingBridge2 />,
        onClick: () => {
          navigate(getNavigatePath(hasModule(ModuleName.SELF_SERVICE), 'mesas'));
          closeMenu();
        },
        show: isFreemium || Boolean(hasModule(ModuleName.SELF_SERVICE)),
        notIncludedOnCurrentPlan: !hasModule(ModuleName.SELF_SERVICE),
      },
      {
        id: 'cupones',
        label: 'Cupones',
        icon: <IconTicket />,
        onClick: () => {
          navigate(getNavigatePath(hasModule(ModuleName.CUPONES), 'cupones'));
          closeMenu();
        },
        show: isFreemium || Boolean(hasModule(ModuleName.CUPONES)),
        notIncludedOnCurrentPlan: !hasModule(ModuleName.CUPONES),
      },
      {
        id: 'usuarios',
        label: 'Alta de usuarios',
        icon: <IconUsers />,
        onClick: () => {
          navigate(getNavigatePath(hasModule(ModuleName.USUARIOS), 'usuarios'));
          closeMenu();
        },
        show: isFreemium || Boolean(hasModule(ModuleName.USUARIOS)),
        notIncludedOnCurrentPlan: !hasModule(ModuleName.USUARIOS),
      },
      {
        id: 'crear-anuncio',
        label: 'Crear anuncio',
        icon: <IconFileText />,
        onClick: () => {
          navigate(getNavigatePath(hasModule(ModuleName.ANUNCIOS), 'anuncios'));
          closeMenu();
        },
        show: isFreemium || Boolean(hasModule(ModuleName.ANUNCIOS)),
        notIncludedOnCurrentPlan: !hasModule(ModuleName.ANUNCIOS),
      },
    ],
    [navigate, hasModule, isFreemium],
  );

  const createOtherMenuItems = (onFounderWarningClick: () => void): MenuItem[] => [
    {
      id: 'ajustes',
      label: 'Ajustes',
      icon: <IconSettings />,
      onClick: () => {
        navigate(getNavigatePath(hasModule(ModuleName.AJUSTES), 'ajustes'));
        closeMenu();
      },
      show: isFreemium || Boolean(hasModule(ModuleName.AJUSTES)),
      notIncludedOnCurrentPlan: !hasModule(ModuleName.AJUSTES),
    },
    {
      id: 'ayudanos-a-mejorar',
      label: 'Ayúdanos a mejorar',
      icon: <IconMessage2Filled />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.FeedbackButtonClicked);
        onFounderWarningClick();
        closeMenu();
      },
      show: true,
    },
    {
      id: 'soporte',
      label: 'Soporte en línea',
      icon: <IconHelpCircle />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.WhatsAppSupportClicked);
        openExternalUrl(EXTERNAL_URLS.SUPPORT_WHATSAPP);
        closeMenu();
      },
      show: true,
    },
    {
      id: 'ir-menu',
      label: 'Ir a mi menú',
      icon: <IconExternalLink />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.MenuViewClicked);
        window.open(`${URL_MENU}/${commerce.id_path}`);
        closeMenu();
      },
      show: true,
    },
    {
      id: 'integraciones',
      label: 'Integraciones',
      icon: <IconCloudCheck />,
      onClick: () => {
        navigate('integraciones');
        closeMenu();
      },
      show: true,
    },
    {
      id: 'cerrar-sesion',
      label: 'Cerrar sesión',
      icon: <IconLogout />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.LogoutClicked);
        auth.logout();
      },
      show: true,
      isRed: true,
    },
  ];

  return { mainMenuItems, createOtherMenuItems };
};
